import React from 'react';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { GroupSeparator, HeadingS } from '@toqio/toqio-web-components';
import type { Locale } from '../../../bridge/protocol';
import type { TransactionGroup } from '../types';
import { EmptyState } from '../../../shared/components';
import { TransactionRow } from './TransactionRow';
import { MOBILE } from './styles';

/**
 * Recent activity region (PRD §3 / §9): the "Recent Activity" title plus a
 * `GroupSeparator` + rows per transaction group. With cards present but no
 * groups, shows the no-activity empty state. (The no-cards case replaces the
 * whole dashboard upstream in `index.tsx`.)
 */
const Section = styled.section`
  display: flex;
  flex-direction: column;

  /* Root drops its side padding on mobile so the card carousel can be
     full-bleed; the activity list re-adds the legacy 16px lum-web gutter. */
  @media (max-width: ${MOBILE}) {
    padding: 0 16px;
  }
`;

/* 16px below the heading to the first date separator; groups then butt
   directly together (0 gap) so the separators sit on the previous row's
   bottom padding — matching the legacy list rhythm. */
const Heading = styled.div`
  margin-bottom: 16px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
`;

type Props = {
  groups: TransactionGroup[];
  locale: Locale;
  onNavigate: (route: string) => void;
};

export const RecentActivity: React.FC<Props> = ({ groups, locale, onNavigate }) => (
  <Section>
    <Heading>
      <HeadingS>
        <Trans>Recent Activity</Trans>
      </HeadingS>
    </Heading>

    {groups.length === 0 ? (
      <EmptyState
        title={t`No payments yet`}
        description={t`You do not have any operations yet. Your transactions will appear here once you start using your card.`}
      />
    ) : (
      groups.map((group, gi) => (
        <Group key={group.title}>
          <GroupSeparator label={group.title} />
          {group.items.map((tx, i) => (
            <TransactionRow
              key={tx.id}
              tx={tx}
              /* Drop the divider on a group's last row only when another date
                 group follows (its separator is the divider); the very last row
                 of the list keeps its border as the closing separator. NB: this
                 matches the live legacy render (measured against the running
                 app), NOT reference/transaction-row.css — that embedded CSS
                 marks every group's last row `no-border`, but the real app keeps
                 the border on the final group's last row. */
              last={i === group.items.length - 1 && gi < groups.length - 1}
              locale={locale}
              onNavigate={onNavigate}
            />
          ))}
        </Group>
      ))
    )}
  </Section>
);
